import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface AccommodationHistoryEntry {
  year: number;
  placeCode: string;
}

export const NO_HISTORY_PLACE_CODE = 'No History';

export async function getCampYear(): Promise<number> {
  const settingsRef = doc(db, 'settings', 'camp');
  const settingsSnap = await getDoc(settingsRef);
  if (settingsSnap.exists() && settingsSnap.data()?.year != null) {
    return Number(settingsSnap.data().year);
  }
  return new Date().getFullYear();
}

export function buildUpdatedAccommodationHistory(
  existingHistory: AccommodationHistoryEntry[],
  campYear: number,
  placeCode: string
): AccommodationHistoryEntry[] {
  const filtered = (existingHistory || []).filter(
    (entry) => Number(entry?.year) !== Number(campYear)
  );

  return [
    { year: campYear, placeCode },
    ...filtered
  ].slice(0, 8);
}

export function buildPrepForNewYearAccommodationHistory(
  existingHistory: AccommodationHistoryEntry[],
  nextCampYear: number
): AccommodationHistoryEntry[] {
  let history = [...(existingHistory || [])];

  if (history.length >= 8) {
    history = history.slice(0, 7);
  }

  const withoutNextYear = history.filter(
    (entry) => Number(entry?.year) !== Number(nextCampYear)
  );

  return [
    { year: nextCampYear, placeCode: NO_HISTORY_PLACE_CODE },
    ...withoutNextYear
  ].slice(0, 8);
}

export async function getCurrentYearPlaceCode(
  primaryCamperId: string,
  campYear?: number
): Promise<string | null> {
  const year = campYear ?? await getCampYear();
  const camperRef = doc(db, 'primaryCampers', primaryCamperId);
  const camperSnap = await getDoc(camperRef);

  if (!camperSnap.exists()) {
    return null;
  }

  const history = camperSnap.data()?.accommodationHistory || [];
  const entry = history.find(
    (item: AccommodationHistoryEntry) => Number(item?.year) === Number(year)
  );

  return entry?.placeCode ?? null;
}

export async function updateCurrentYearAccommodationHistory(
  primaryCamperId: string,
  placeCode: string,
  campYear?: number
): Promise<void> {
  const year = campYear ?? await getCampYear();
  const camperRef = doc(db, 'primaryCampers', primaryCamperId);
  const camperSnap = await getDoc(camperRef);

  if (!camperSnap.exists()) {
    throw new Error('Primary camper not found');
  }

  const existingHistory = camperSnap.data()?.accommodationHistory || [];
  const updatedHistory = buildUpdatedAccommodationHistory(existingHistory, year, placeCode);

  await updateDoc(camperRef, { accommodationHistory: updatedHistory });
}

export async function clearCurrentYearAccommodationHistory(
  primaryCamperId: string,
  campYear?: number
): Promise<void> {
  await updateCurrentYearAccommodationHistory(primaryCamperId, NO_HISTORY_PLACE_CODE, campYear);
}

export function isTrackedAccommodationPlaceCode(placeCode: string | null | undefined): boolean {
  if (!placeCode) {
    return false;
  }

  const normalized = placeCode.trim();
  return normalized !== '' && normalized !== NO_HISTORY_PLACE_CODE && normalized !== 'Did Not Attend';
}
